import { useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/ui/select';
import { useFeedbackTerminology } from '@/entities/workspace/hooks';
import { useSignalsSearchParams } from '@/pages/workspace/signals/search-params-schema';
import { SortPopover, type SortPopoverOptions } from '@/shared/ui/sort-popover';

import { getPeriodLabel } from '../../lib/signal-date-preset.utils';
import {
  SIGNAL_PAGE_VIEW_TYPE,
  type SignalDateSelectionInput,
} from '../../model';

const TOP_COUNT_OPTIONS = [5, 10, 20, 30, 50, 100] as const;

const SORT_OPTIONS: SortPopoverOptions[] = [
  {
    sortOption: { label: 'Feedback Count', value: 'feedback' },
    orderOptions: [
      { label: 'Least to most', value: 'asc' },
      { label: 'Most to least', value: 'desc' },
    ],
  },
  {
    sortOption: { label: '% Change', value: 'changes' },
    orderOptions: [
      { label: 'Least changed', value: 'asc' },
      { label: 'Most changed', value: 'desc' },
    ],
  },
];

type Props = {
  datePreset: SignalDateSelectionInput;
};

export default function ViewControlHeaderSorter({ datePreset }: Props) {
  const [params, setParams] = useSignalsSearchParams();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();

  const options = useMemo(() => {
    return SORT_OPTIONS.map((option) => ({
      ...option,
      sortOption:
        option.sortOption.value === 'changes'
          ? {
              ...option.sortOption,
              label: `${getPeriodLabel(datePreset)} change`,
            }
          : {
              ...option.sortOption,
              label: replaceFeedbackTerminology('Feedback Count'),
            },
    }));
  }, [datePreset, replaceFeedbackTerminology]);

  return (
    <div className="flex items-center gap-2">
      {params.view !== SIGNAL_PAGE_VIEW_TYPE.LIST && (
        <Select
          value={params.topCount.toString()}
          onValueChange={(value) => setParams({ topCount: Number(value) })}
        >
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TOP_COUNT_OPTIONS.map((count) => (
              <SelectItem key={count} value={count.toString()}>
                Top {count}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <SortPopover
        options={options}
        sortValue={params.sortBy}
        orderValue={params.sortOrder}
        onChangeSort={({ sortValue, orderValue }) => {
          setParams({
            sortBy: sortValue as 'feedback' | 'changes',
            sortOrder: orderValue as 'asc' | 'desc',
          });
        }}
      />
    </div>
  );
}
